import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { orgQuery, withOrg } from "@/lib/org";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { useStudents } from "@/hooks/useStudents";
import { buildApartmentIndex, PATTERN_LABEL, encodeApartmentId, ApartmentAggregate } from "@/lib/apartmentInfer";
import { LinePattern } from "@/lib/apartmentLines";
import { useAuth } from "@/store/auth";
import clsx from "clsx";
import { Building2, Search, CheckCircle2, CircleAlert, Zap, User, MapPin, Sparkles } from "lucide-react";

import { plainError } from "@/lib/plainError";
/**
 * 단지 관리 — 자동 추론 결과 확인 + 패턴 수정.
 * 수정 시 apartments/{id} 문서에 verified 필드로 기록 (소스: admin/driver).
 */
export default function ApartmentsPage() {
  const { user } = useAuth();
  const { students } = useStudents();
  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [q, setQ] = useState("");
  const [guFilter, setGuFilter] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(orgQuery("apartments"), (snap) => {
      const m: Record<string, any> = {};
      snap.docs.forEach((d) => { m[d.id] = d.data(); });
      setOverrides(m);
    });
    return () => unsub();
  }, []);

  const apartments = useMemo(() => buildApartmentIndex(students), [students]);
  // 권역 시군구 목록 — 데이터에서 도출(전국화)
  const guList = useMemo(
    () => Array.from(new Set(apartments.map((a) => a.gu).filter(Boolean))).sort(),
    [apartments],
  );

  const filtered = useMemo(() => {
    return apartments.filter((a) => {
      if (guFilter && a.gu !== guFilter) return false;
      if (q.trim()) {
        const t = q.trim();
        return a.name.includes(t) || a.dong.includes(t);
      }
      return true;
    });
  }, [apartments, q, guFilter]);

  const savePattern = async (apt: ApartmentAggregate, pattern: LinePattern, note: string = "") => {
    await setDoc(doc(db, "apartments", apt.id), withOrg({
      name: apt.name,
      gu: apt.gu,
      dong: apt.dong,
      householdCount: apt.householdCount,
      buildings: apt.buildings,
      inferred: apt.inferred,
      verified: {
        pattern,
        verifiedBy: user?.email || user?.uid,
        verifiedAt: serverTimestamp(),
        source: "admin",
        note,
      },
      updatedAt: serverTimestamp(),
    }), { merge: true });
  };

  // AI 라인 추론 — 단지 정보를 Claude 로 보내 패턴 추론 후 verified(source: ai)로 저장
  const aiInfer = async (apt: ApartmentAggregate) => {
    const fn = httpsCallable(functions, "reviewApartmentLine");
    const res = await fn({
      name: apt.name,
      gu: apt.gu,
      dong: apt.dong,
      buildingCount: apt.buildings.length,
      householdCount: apt.householdCount,
      maxUnit: apt.inferred.maxUnit,
      maxFloor: apt.inferred.maxFloor,
      sampleHo: Object.values(apt.sampleHoByBuilding).flat().slice(0, 12),
    });
    const { pattern, confidence, reasoning } = res.data as {
      pattern: LinePattern;
      confidence: string;
      reasoning: string;
    };
    await setDoc(doc(db, "apartments", apt.id), withOrg({
      name: apt.name,
      gu: apt.gu,
      dong: apt.dong,
      householdCount: apt.householdCount,
      buildings: apt.buildings,
      inferred: apt.inferred,
      verified: {
        pattern,
        verifiedBy: "AI 추론",
        verifiedAt: serverTimestamp(),
        source: "ai",
        note: `AI(${confidence}): ${reasoning}`,
      },
      updatedAt: serverTimestamp(),
    }), { merge: true });
  };

  // 통계
  const totalApt = apartments.length;
  const verifiedCount = apartments.filter((a) => overrides[a.id]?.verified?.pattern).length;
  const lowConfCount = apartments.filter((a) => (a.inferred.confidence < 0.5) && !overrides[a.id]?.verified?.pattern).length;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2"><Building2 className="w-6 h-6 text-brand-500" /> 아파트 라인 관리</h1>
          <p className="text-sm text-slate-500 mt-1">
            각 단지의 엘리베이터/라인 구조를 자동 추론하고, 필요 시 관리자가 수정합니다. 기사 앱에서도 현장 피드백으로 수정 가능합니다.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="card px-3 py-2 text-xs flex items-center gap-2">
            <span className="text-slate-500">총</span>
            <strong className="text-slate-800">{totalApt}</strong>
            <span>·</span>
            <span className="text-emerald-600">✓ 확인 {verifiedCount}</span>
            <span>·</span>
            <span className="text-amber-600">⚠ 저신뢰 {lowConfCount}</span>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            className="input pl-9 w-64"
            placeholder="아파트 / 동 이름 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select className="input w-auto" value={guFilter} onChange={(e) => setGuFilter(e.target.value)}>
          <option value="">전체 구</option>
          {guList.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} 단지</div>
      </div>

      {/* 리스트 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((apt) => {
          const ov = overrides[apt.id];
          const finalPattern: LinePattern = ov?.verified?.pattern || apt.inferred.pattern;
          const pLabel = PATTERN_LABEL[finalPattern];
          const verified = !!ov?.verified?.pattern;
          const lowConf = apt.inferred.confidence < 0.5 && !verified;
          return (
            <div
              key={apt.id}
              className={clsx(
                "card p-4 cursor-pointer hover:shadow-elevated transition",
                verified && "ring-1 ring-emerald-200",
                lowConf && "ring-1 ring-amber-200",
              )}
              onClick={() => setOpenId(apt.id === openId ? null : apt.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-2xs text-slate-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {apt.gu} {apt.dong}
                  </div>
                  <div className="font-bold text-slate-900 truncate mt-0.5">{apt.name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-extrabold text-brand-700 leading-tight">{apt.householdCount}</div>
                  <div className="text-2xs text-slate-400">가구</div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-xl">{pLabel.emoji}</span>
                <span className="font-semibold">{pLabel.short}</span>
                {verified ? (
                  <span className="chip-success ml-auto"><CheckCircle2 className="w-3 h-3" /> 확정</span>
                ) : (
                  <ConfidenceBadge conf={apt.inferred.confidence} />
                )}
              </div>

              <div className="mt-2 text-xs text-slate-500">
                🏢 {apt.buildings.length}개 동 · 가장 큰 호수 {apt.inferred.maxUnit} · 표본 {apt.inferred.sampleSize}집
              </div>

              {openId === apt.id && (
                <DetailPanel apt={apt} currentPattern={finalPattern} onSave={savePattern} onAiInfer={aiInfer} override={ov} />
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="card p-10 text-center text-slate-500 text-sm">
          표시할 단지가 없습니다. 학생 데이터 업로드 후 나타납니다.
        </div>
      )}
    </div>
  );
}

function ConfidenceBadge({ conf }: { conf: number }) {
  if (conf >= 0.8) return <span className="chip-success ml-auto"><Zap className="w-3 h-3" /> 추론 {Math.round(conf * 100)}%</span>;
  if (conf >= 0.5) return <span className="chip-brand ml-auto"><Zap className="w-3 h-3" /> 추론 {Math.round(conf * 100)}%</span>;
  return <span className="chip-warning ml-auto"><CircleAlert className="w-3 h-3" /> 추론 {Math.round(conf * 100)}%</span>;
}

function DetailPanel({
  apt, currentPattern, onSave, onAiInfer, override,
}: {
  apt: ApartmentAggregate;
  currentPattern: LinePattern;
  onSave: (apt: ApartmentAggregate, pattern: LinePattern, note?: string) => Promise<void>;
  onAiInfer: (apt: ApartmentAggregate) => Promise<void>;
  override?: any;
}) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const patterns: LinePattern[] = ["1-2|3-4", "1-2-3-4", "corridor", "1|2|3|4"];

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 animate-fade-in" onClick={(e) => e.stopPropagation()}>
      <div>
        <div className="label">추론 근거</div>
        <div className="text-xs text-slate-600 mt-1">{apt.inferred.evidence}</div>
      </div>

      <button
        className="btn-secondary w-full text-xs"
        disabled={aiBusy}
        onClick={async () => {
          setAiBusy(true);
          try { await onAiInfer(apt); } catch (e: any) { alert("동·호 짐작을 못 했습니다 — " + plainError(e)); } finally { setAiBusy(false); }
        }}
      >
        <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? "AI 추론 중…" : "AI로 라인 추론 (불확정 시)"}
      </button>

      <div>
        <div className="label">동별 관측 호수 샘플</div>
        <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
          {Object.entries(apt.sampleHoByBuilding).map(([b, hos]) => (
            <div key={b} className="text-xs flex gap-2">
              <span className="font-semibold text-slate-600 w-10">{b}동:</span>
              <span className="text-slate-500">{hos.join(", ")}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="label mb-1">패턴 지정</div>
        <div className="grid grid-cols-2 gap-1">
          {patterns.map((p) => {
            const lbl = PATTERN_LABEL[p];
            const active = currentPattern === p;
            return (
              <button
                key={p}
                className={clsx(
                  "rounded-xl border px-3 py-2 text-left text-xs transition",
                  active
                    ? "border-brand-400 bg-brand-50 text-brand-800 shadow-soft"
                    : "border-slate-200 bg-white hover:border-slate-300",
                )}
                onClick={async () => {
                  setSaving(true);
                  try { await onSave(apt, p, note); } finally { setSaving(false); }
                }}
                disabled={saving}
              >
                <div className="font-bold">{lbl.emoji} {lbl.short}</div>
                <div className="text-slate-500 mt-0.5 text-2xs">{lbl.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <input
          className="input text-xs"
          placeholder="메모 (예: 1동은 복도식, 2동은 판상형)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {override?.verified && (
        <div className="text-2xs text-slate-500 flex items-center gap-1">
          <User className="w-3 h-3" /> {override.verified.verifiedBy} · {override.verified.source === "admin" ? "사장님 확인" : override.verified.source === "driver" ? "기사 확인" : override.verified.source}
          {override.verified.note && <span className="text-slate-400"> · "{override.verified.note}"</span>}
        </div>
      )}
    </div>
  );
}
